import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { PatientMeeting, MeetingType } from '../../types/api';
import { getMeetingTypeName } from '../../utils/meetingUtils';

interface UploadTranscriptModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetings: PatientMeeting[];
    meetingTypes: MeetingType[];
    onUpload: (meetingId: number, filename: string, text: string) => Promise<void>;
    isUploading?: boolean;
    preSelectedMeetingId?: number;
}

export const UploadTranscriptModal: React.FC<UploadTranscriptModalProps> = ({
    isOpen,
    onClose,
    meetings,
    meetingTypes,
    onUpload,
    isUploading = false,
    preSelectedMeetingId,
}) => {
    const [selectedMeetingId, setSelectedMeetingId] = useState<number>(preSelectedMeetingId || 0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && preSelectedMeetingId) {
            setSelectedMeetingId(preSelectedMeetingId);
        }
    }, [isOpen, preSelectedMeetingId]);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check file extension
            const fileName = file.name.toLowerCase();
            if (!fileName.endsWith('.vtt') && !fileName.endsWith('.txt')) {
                setError('Please select a .vtt or .txt file');
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
            setError(null);
        }
    };

    const readFileAsText = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = () => {
                resolve(reader.result as string);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file');
            return;
        }

        if (!selectedMeetingId) {
            setError('Please select a meeting');
            return;
        }

        try {
            setError(null);
            const text = await readFileAsText(selectedFile);
            await onUpload(selectedMeetingId, selectedFile.name, text);
            // Reset form
            setSelectedFile(null);
            setSelectedMeetingId(0);
            // onClose will be called by parent after successful upload
        } catch (err: any) {
            setError(err.message || 'Failed to upload transcript');
        }
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setSelectedMeetingId(0);
        setError(null);
        onClose();
    };

    // Format date for dropdown display
    const formatDate = (timestamp: number | undefined | null) => {
        if (!timestamp || timestamp === 0) return "";
        const date = new Date(timestamp);
        return date.toLocaleDateString("en-GB") + " " + date.toLocaleTimeString("en-GB", {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getMeetingLabel = (meeting: PatientMeeting) => {
        return `${formatDate(meeting.date)} - ${getMeetingTypeName(meeting, meetingTypes)}`;
    };

    return (
        <ModalWrapper modalId="UploadTranscriptModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Upload Transcript"
            isLoading={isUploading}
        >
            {/* Meeting Type Dropdown */}
            <div className="mb-4">
                <div className="relative">
                    <select
                        value={selectedMeetingId}
                        onChange={(e) => setSelectedMeetingId(Number(e.target.value))}
                        className="w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none pr-10 text-[#366A75]"
                        disabled={isUploading}
                    >
                        <option value={0}>Select Meeting</option>
                        {meetings.map((meeting) => (
                            <option key={meeting.id} value={meeting.id}>
                                {getMeetingLabel(meeting)}
                            </option>
                        ))}
                    </select>
                    {/* Dropdown arrow */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* File Input */}
            <div className="mb-6">
                <div className="relative">
                    <input
                        type="file"
                        accept=".vtt,.txt"
                        onChange={handleFileChange}
                        className="hidden"
                        id="transcript-upload"
                        disabled={isUploading}
                    />
                    <label
                        htmlFor="transcript-upload"
                        className="w-full px-4 py-2 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] flex items-center justify-between cursor-pointer hover:bg-[#D0E7E5]"
                    >
                        <span className="text-[#366A75] text-sm">
                            {selectedFile ? selectedFile.name : 'No file selected'}
                        </span>
                        <svg className="w-5 h-5 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                    </label>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Save and Cancel Buttons */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className={POPUP_STYLES.button}
                >
                    {isUploading ? 'Uploading...' : 'Save'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isUploading}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
